import { ChatDetailsResponse } from '@choco-admin/types';
import { Button, Chip, Avatar } from '@heroui/react';
import React, { useState } from 'react';
import {
  FiEdit,
  FiMapPin,
  FiShoppingBag,
  FiExternalLink,
  FiAlertTriangle,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import {
  EntitySearchModal,
  type EntitySearchOption,
} from '../../../../../../../components/FormComponents';
import { FormFooter } from '../../../../../../../components/FormFooter';
import { useNotification } from '../../../../../../../contexts/NotificationContext';
import { useSearchBuyersForChat } from '../../../../../hooks/useSearchBuyersForChat';
import { useSearchBuyerUsers } from '../../../../../hooks/useSearchBuyerUsersForChat';
import { useUpdateChatBuyer } from '../../../../../hooks/useUpdateChatBuyer';
import { useChatPageContext } from '../../../contexts';

interface BuyerMetadata {
  email?: string;
  phone?: string;
  whatsApp?: boolean;
  sms?: boolean;
  fax?: boolean;
  orderPreferences?: { type: string }[];
  rolesEnabled?: boolean;
  fullAddress?: string;
  customerNumberMandatory?: boolean;
  referenceId?: string;
  name?: string;
}

type BuyerEntitySearchOption = EntitySearchOption & {
  metadata?: BuyerMetadata;
};

interface BuyerSectionProps {
  chatData: ChatDetailsResponse;
  chatId: string;
}

export const BuyerSection: React.FC<BuyerSectionProps> = ({
  chatData,
  chatId,
}) => {
  const { chatData: contextChatData } = useChatPageContext();
  const currentChatData = contextChatData || chatData;
  const currentBuyer = currentChatData.buyerEntity;
  const navigate = useNavigate();

  const [isChangeBuyerModalOpen, setIsChangeBuyerModalOpen] = useState(false);
  const [selectedBuyerId, setSelectedBuyerId] = useState<string | null>(null);

  const { addNotification } = useNotification();

  const {
    buyers,
    isLoading: searchLoading,
    searchBuyers,
  } = useSearchBuyersForChat();
  const { users: buyerUsers, isLoading: usersLoading } = useSearchBuyerUsers({
    buyerId: selectedBuyerId || '',
  });

  const { updateChatBuyer, isUpdating } = useUpdateChatBuyer({
    onSuccess: () => {
      addNotification({
        title: 'Buyer Updated',
        message: 'The chat buyer has been updated successfully.',
        type: 'success',
      });
      setIsChangeBuyerModalOpen(false);
      setSelectedBuyerId(null);
    },
    onError: (error) => {
      addNotification({
        title: 'Update Failed',
        message: error.message,
        type: 'error',
      });
    },
  });

  const handleBuyerSelect = (selectedBuyer: BuyerEntitySearchOption) => {
    setSelectedBuyerId(selectedBuyer.id);
  };

  const handleConfirm = async (selectedBuyer: BuyerEntitySearchOption) => {
    if (!selectedBuyer.id) return;

    await updateChatBuyer({
      chatId,
      buyerId: selectedBuyer.id,
      buyerUserIds: buyerUsers.map((user) => user.value),
    });
  };

  const handleCancel = () => {
    setIsChangeBuyerModalOpen(false);
    setSelectedBuyerId(null);
  };

  const handleBuyerClick = () => {
    if (currentBuyer?.id) {
      navigate(`/buyers/${currentBuyer.id}`);
    }
  };

  const renderBuyerDetails = (buyerEntity: BuyerEntitySearchOption) => (
    <div className="space-y-2">
      <h4 className="font-semibold text-foreground">{buyerEntity.name}</h4>
      {buyerEntity.metadata?.fullAddress && (
        <div className="flex items-start gap-2">
          <FiMapPin className="w-4 h-4 mt-0.5 text-default-500" />
          <p className="text-sm text-foreground-500">
            {buyerEntity.metadata.fullAddress}
          </p>
        </div>
      )}
      {buyerEntity.metadata?.referenceId && (
        <p className="text-sm text-foreground-500">
          ID: {buyerEntity.metadata.referenceId}
        </p>
      )}
    </div>
  );

  const renderUsersList = () => {
    if (usersLoading) {
      return <p className="text-sm text-foreground-500">Loading users...</p>;
    }

    if (buyerUsers.length === 0) {
      return (
        <p className="text-sm text-foreground-500">
          No users found for this buyer
        </p>
      );
    }

    return (
      <ul className="space-y-2">
        {buyerUsers.map((user) => (
          <li key={user.value} className="text-sm text-foreground-500">
            {user.label}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-default-500 uppercase tracking-wider flex items-center gap-2">
          <FiShoppingBag className="w-4 h-4" /> Primary Buyer
        </h4>
        <Button
          size="sm"
          variant="flat"
          color="primary"
          startContent={<FiEdit className="w-4 h-4" />}
          onPress={() => setIsChangeBuyerModalOpen(true)}
        >
          Change Buyer
        </Button>
      </div>

      {/* Buyer Card */}
      {currentBuyer ? (
        <div
          className="group relative overflow-hidden rounded-xl border border-default-200 bg-content1 p-4 transition-all hover:border-primary/50 hover:shadow-md cursor-pointer"
          onClick={handleBuyerClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleBuyerClick();
            }
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar
                name={currentBuyer.name}
                className="h-12 w-12 text-lg"
                isBordered
                color="primary"
                radius="lg"
              />
              <div>
                <h4 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                  {currentBuyer.name}
                </h4>
                {currentBuyer.referenceId && (
                  <p className="text-sm text-default-500">
                    ID:{' '}
                    <span className="font-mono">
                      {currentBuyer.referenceId}
                    </span>
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Chip
                color="success"
                variant="flat"
                size="sm"
                className="capitalize"
              >
                Active
              </Chip>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <FiExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-default-300 bg-default-50 p-6 text-center">
          <FiShoppingBag className="mx-auto h-8 w-8 text-default-300 mb-2" />
          <p className="text-default-500">No buyer selected</p>
        </div>
      )}

      {/* Warning Box */}
      <div className="rounded-xl border border-warning-200 bg-warning-50/50 dark:bg-warning-900/10 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-warning-100 p-2 text-warning-600 dark:bg-warning-900/30 dark:text-warning-400">
            <FiAlertTriangle className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <h5 className="text-sm font-semibold text-warning-800 dark:text-warning-200">
              Changing Buyer Entity
            </h5>
            <p className="mt-1 text-sm text-warning-700 dark:text-warning-300 leading-relaxed">
              Changing the buyer entity will immediately clear all users (both
              buyer and distributor) from this chat. You will need to re-add
              them manually.
            </p>
          </div>
        </div>
      </div>

      <EntitySearchModal
        isOpen={isChangeBuyerModalOpen}
        onClose={handleCancel}
        title="Change Chat Buyer"
        searchPlaceholder="Search buyers by name or address..."
        entityType="buyer"
        isSearching={searchLoading}
        searchResults={buyers.map((buyerOption) => ({
          id: buyerOption.value,
          name: buyerOption.label,
          label: buyerOption.label,
          subtitle: buyerOption.metaData?.fullAddress || '',
          metadata: {
            fullAddress: buyerOption.metaData?.fullAddress || '',
            name: buyerOption.metaData?.name || '',
            referenceId: buyerOption.metaData?.referenceId || '',
          },
        }))}
        onSearch={searchBuyers}
        onConfirm={handleConfirm}
        isConfirming={isUpdating}
        onSelect={handleBuyerSelect}
        renderConfirmation={(selectedBuyer) => (
          <div className="space-y-4">
            <div className="p-4 bg-primary-50 rounded-lg">
              {renderBuyerDetails(selectedBuyer)}
            </div>

            <div className="p-4 bg-content2 rounded-lg space-y-2">
              <h4 className="font-semibold text-foreground">
                Users to be added
              </h4>
              {renderUsersList()}
            </div>

            <FormFooter
              saveText="Update Buyer"
              onSave={() => handleConfirm(selectedBuyer)}
              onCancel={handleCancel}
              isSaving={isUpdating}
              isSaveDisabled={!selectedBuyerId || usersLoading}
            />
          </div>
        )}
      />
    </div>
  );
};
